import { useParams, Link } from 'react-router-dom';
import { articles, categories } from '@/mocks/data';
import Breadcrumb from '@/components/feature/Breadcrumb';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = articles.find((a) => a.id === Number(id));

  if (!article) {
    return (
      <div className="w-full px-4 md:px-6 py-20 text-center">
        <div className="w-16 h-16 mx-auto mb-4 bg-background-100 rounded-full flex items-center justify-center">
          <i className="ri-file-unknow-line text-2xl text-foreground-300"></i>
        </div>
        <h1 className="text-xl font-heading font-semibold text-foreground-900 mb-2">
          文章未找到
        </h1>
        <p className="text-sm text-foreground-500 mb-6">
          您访问的文章不存在或已被移除
        </p>
        <Link
          to="/"
          className="inline-flex items-center gap-1 px-5 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors"
        >
          <i className="ri-arrow-left-line"></i> 返回首页
        </Link>
      </div>
    );
  }

  const catInfo = categories.find((c) => c.slug === article.category);

  const categoryLabel =
    article.category === 'guide' ? '使用指南' :
    article.category === 'resource' ? '资源推荐' :
    article.category === 'member' ? '会员专区' :
    article.category === 'news' ? '最新动态' :
    article.category === 'tips' ? '实用技巧' : '社区互动';

  const relatedArticles = articles
    .filter((a) => a.category === article.category && a.id !== article.id)
    .slice(0, 3);

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    datePublished: article.date,
    url: `https://www.91uncle.com/article/${article.id}`,
    author: {
      '@type': 'Organization',
      name: '91叔叔',
    },
    publisher: {
      '@type': 'Organization',
      name: '91叔叔',
    },
    image: article.image,
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(articleJsonLd)}</script>
      <Breadcrumb
        items={[
          { label: '首页', to: '/' },
          { label: categoryLabel, to: `/category/${article.category}` },
          { label: article.title },
        ]}
      />

      <article className="w-full px-4 md:px-6 py-10 bg-background-50">
        <div className="max-w-4xl mx-auto">
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-4 flex-wrap">
              <span className="text-xs bg-accent-100 text-accent-700 px-3 py-1 rounded-full font-medium">
                {categoryLabel}
              </span>
              <span className="text-sm text-foreground-400">{article.date}</span>
              <span className="text-sm text-foreground-400">{article.readTime}</span>
            </div>
            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-4 leading-tight">
              {article.title}
            </h1>
            <p className="text-base text-foreground-500 leading-relaxed">
              {article.excerpt}
            </p>
          </div>

          <div className="w-full h-64 md:h-96 rounded-xl overflow-hidden mb-10">
            <img
              src={article.image}
              alt={article.title}
              title={`${article.title} - 91叔叔`}
              className="w-full h-full object-cover object-top"
            />
          </div>

          <div className="prose-custom text-foreground-800 leading-relaxed text-[15px]">
            {article.content.split('\n').map((paragraph, idx) => {
              const trimmed = paragraph.trim();
              if (!trimmed) return null;

              if (trimmed.startsWith('## ')) {
                return (
                  <h2 key={idx} className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mt-10 mb-4">
                    {trimmed.replace('## ', '')}
                  </h2>
                );
              }

              if (trimmed.startsWith('### ')) {
                return (
                  <h3 key={idx} className="text-lg font-heading font-semibold text-foreground-800 mt-8 mb-3">
                    {trimmed.replace('### ', '')}
                  </h3>
                );
              }

              return (
                <p key={idx} className="mb-4">
                  {trimmed}
                </p>
              );
            })}
          </div>

          {relatedArticles.length > 0 && (
            <div className="mt-16 pt-10 border-t border-background-200/70">
              <h3 className="text-lg font-heading font-semibold text-foreground-900 mb-6">
                相关推荐
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                {relatedArticles.map((ra) => (
                  <Link
                    key={ra.id}
                    to={`/article/${ra.id}`}
                    className="group bg-background-50 rounded-xl overflow-hidden border border-background-200/70 transition-all duration-300 hover:scale-[1.02]"
                  >
                    <div className="w-full h-36 overflow-hidden">
                      <img
                        src={ra.image}
                        alt={ra.title}
                        title={`${ra.title} - 91叔叔`}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-4">
                      <h4 className="text-sm font-heading font-semibold text-foreground-900 line-clamp-2 group-hover:text-primary-600 transition-colors">
                        {ra.title}
                      </h4>
                      <span className="text-xs text-foreground-400 mt-2 block">{ra.date}</span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </article>
    </>
  );
}